import React, { createContext, useContext, useState, useEffect, useCallback, useMemo, useRef } from 'react';
import axios from 'axios';
import { useAppBridge } from '@shopify/app-bridge-react';
import { Redirect } from '@shopify/app-bridge/actions';
import { getSessionToken } from '@shopify/app-bridge/utilities';
import { api } from '../utils/api';

const API_BASE_URL = process.env.REACT_APP_API_URL || '';
const AuthContext = createContext(null);

/**
 * AuthProvider - Manages authentication state for embedded Shopify app.
 *
 * On mount, performs a one-time bootstrap: exchanges a Shopify session token
 * to verify the store is installed, then wires up a session-token provider
 * so the API client fetches a fresh token before every backend request
 * (Shopify gold-standard pattern).
 */
export function AuthProvider({ children }) {
  const app = useAppBridge();
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [authError, setAuthError] = useState(null);
  const [shop, setShop] = useState(null);
  const bootstrapRan = useRef(false);

  // Get shop from URL params
  const urlShop = useMemo(() => {
    const params = new URLSearchParams(window.location.search);
    return params.get('shop');
  }, []);

  // One-time bootstrap: verify store is installed, then wire up per-request tokens
  useEffect(() => {
    // Guard against double-fire (React.StrictMode in dev, or rapid remount)
    if (bootstrapRan.current) return;
    bootstrapRan.current = true;

    // Clear any stale singleton state from a previous iframe load
    api.clearTokenCache();

    // Wire up the per-request token provider SYNCHRONOUSLY before any async
    // work. If a child component fires an API call before bootstrap finishes
    // (e.g. Billing.js's mount-effect after a Shopify-approval redirect), the
    // request interceptor needs a way to exchange tokens - otherwise the
    // request goes out with no Authorization header and 401s. Setting the
    // provider here lets the interceptor's _getAppToken call do its own
    // /auth/token exchange even before our bootstrap below completes.
    if (urlShop) {
      api.setSessionTokenProvider(() => getSessionToken(app), urlShop);
    } else {
      api.setSessionTokenProvider(null, null);
    }

    const checkAuth = async () => {
      try {
        const currentShop = urlShop;
        setShop(currentShop);

        if (!currentShop) {
          setAuthError('Missing shop parameter. Please access the app from Shopify admin.');
          setIsLoading(false);
          return;
        }

        // Get Shopify session token from App Bridge
        const shopifySessionToken = await getSessionToken(app);

        // Use raw axios to bypass the interceptor - this is a bootstrap call,
        // not a normal API request. Prevents the interceptor from triggering
        // an extra token exchange if sessionTokenProvider was stale.
        const response = await axios.post(`${API_BASE_URL}/auth/token`, {
          shop: currentShop,
          session_token: shopifySessionToken,
        });
        const data = typeof response.data === 'string'
          ? JSON.parse(response.data) : response.data;

        if (data.token) {
          // Provider was already wired sync above - just prime the cache so
          // the next request uses this token instead of triggering another
          // exchange.
          api.primeTokenCache(data.token);

          setIsAuthenticated(true);
        }
      } catch (error) {
        // Check if store needs to be installed
        if (error.response?.status === 404) {
          // Use App Bridge Redirect for OAuth flow - point to backend, not frontend
          const apiBase = process.env.REACT_APP_API_URL || window.location.origin;
          const installUrl = `${apiBase}/auth/install?shop=${urlShop}`;
          const redirect = Redirect.create(app);
          redirect.dispatch(Redirect.Action.REMOTE, installUrl);
          return;
        }
        setAuthError('Failed to authenticate. Please reinstall the app.');
      } finally {
        setIsLoading(false);
      }
    };

    checkAuth();
  }, [app, urlShop]);

  const logout = useCallback(async () => {
    try {
      await api.post('/auth/logout');
    } catch (error) {
      // Logout failed silently
    }

    api.clearTokenCache();
    api.setSessionTokenProvider(null, null);
    setIsAuthenticated(false);
  }, []);

  const value = useMemo(() => ({
    isAuthenticated,
    isLoading,
    authError,
    shop,
    logout,
  }), [isAuthenticated, isLoading, authError, shop, logout]);

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
